import hekateBus from '../../bus';
import ExternalUtil from '../ExternalUtil';

type GenericMapItem = {
  from: string | null;
  to: string | null;
};

type GenericMap = Array<GenericMapItem>;

let isInit = false;

export type ServiceEndpointsQueryResult = {
  wwwDomainToEnvMap: GenericMap;
  wwwEnvToCadEnvMap: GenericMap;
  cadEnvToCadDomainMap: GenericMap;
  cadEnvToCadSsoDomainMap: GenericMap;
  firebaseDevFunctionsMap: GenericMap;
};

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export default class ServiceEndpointsUtil {
  private static wwwDomainToEnvMap: GenericMap = [];

  private static wwwEnvToCadEnvMap: GenericMap = [];

  private static cadEnvToCadDomainMap: GenericMap = [];

  private static cadEnvToCadSsoDomainMap: GenericMap = [];

  private static firebaseDevFunctionsMap: GenericMap = [];

  private static mapFbFunction = (functionPath: string) => {
    if (ExternalUtil.isProduction()) return functionPath;

    const fbMap = ServiceEndpointsUtil.firebaseDevFunctionsMap;

    const from = functionPath.split('/api/fb/')[1];
    if (!from) {
      return functionPath;
    }

    const found = fbMap?.find((val) => val.from === from);
    return found && found.to ? `/api/fb/${found.to}` : functionPath;
  };

  public static crossSessionEndpoint = ServiceEndpointsUtil.mapFbFunction('/api/fb/crossSession');

  public static autoCompleteEndpoint = ServiceEndpointsUtil.mapFbFunction('/api/fb/autoSelect');

  public static submitFormResolutionEndpoint = ServiceEndpointsUtil.mapFbFunction(
    '/api/fb/submitFormResolution'
  );

  public static submitFormEndpoint = ServiceEndpointsUtil.mapFbFunction('/api/fb/submitForm');

  public static signupEmailCheckEndpoint = ServiceEndpointsUtil.mapFbFunction(
    '/api/fb/signupEmailCheck'
  );

  public static signupEmailStatusEndpoint = ServiceEndpointsUtil.mapFbFunction(
    '/api/fb/signupEmailStatus'
  );

  public static refCodeCheckEndpoint = ServiceEndpointsUtil.mapFbFunction('/api/fb/refCodeCheck');

  public static csrfTokenTestEndpoint = ServiceEndpointsUtil.mapFbFunction('/api/fb/csrfTokenTest');

  public static initListener(): void {
    // Only run in non-production environments
    if (ExternalUtil.isProduction()) return;

    if (isInit) return;
    isInit = true;

    // Listen for service endpoint data
    hekateBus.addEventListener('service-endpoints-ready', ({ data }: { data: unknown }) => {
      const serviceData = data as ServiceEndpointsQueryResult;
      // Update mappings
      ServiceEndpointsUtil.cadEnvToCadDomainMap = serviceData?.cadEnvToCadDomainMap || [];
      ServiceEndpointsUtil.wwwDomainToEnvMap = serviceData?.wwwDomainToEnvMap || [];
      ServiceEndpointsUtil.wwwEnvToCadEnvMap = serviceData?.wwwEnvToCadEnvMap || [];
      ServiceEndpointsUtil.firebaseDevFunctionsMap = serviceData?.firebaseDevFunctionsMap || [];
      ServiceEndpointsUtil.cadEnvToCadSsoDomainMap = serviceData?.cadEnvToCadSsoDomainMap || [];

      // Update endpoints
      ServiceEndpointsUtil.crossSessionEndpoint =
        ServiceEndpointsUtil.mapFbFunction('/api/fb/crossSession');
      ServiceEndpointsUtil.autoCompleteEndpoint =
        ServiceEndpointsUtil.mapFbFunction('/api/fb/autoSelect');
      ServiceEndpointsUtil.submitFormResolutionEndpoint = ServiceEndpointsUtil.mapFbFunction(
        '/api/fb/submitFormResolution'
      );
      ServiceEndpointsUtil.submitFormEndpoint =
        ServiceEndpointsUtil.mapFbFunction('/api/fb/submitForm');
      ServiceEndpointsUtil.signupEmailCheckEndpoint = ServiceEndpointsUtil.mapFbFunction(
        '/api/fb/signupEmailCheck'
      );
      ServiceEndpointsUtil.signupEmailStatusEndpoint = ServiceEndpointsUtil.mapFbFunction(
        '/api/fb/signupEmailStatus'
      );
      ServiceEndpointsUtil.refCodeCheckEndpoint =
        ServiceEndpointsUtil.mapFbFunction('/api/fb/refCodeCheck');
      ServiceEndpointsUtil.csrfTokenTestEndpoint =
        ServiceEndpointsUtil.mapFbFunction('/api/fb/csrfTokenTest');
    });

    // Request service endpoint data
    hekateBus.dispatchEvent('request-service-endpoints', {});
    setTimeout(() => {
      hekateBus.dispatchEvent('request-service-endpoints', {});
    }, 3000); // Handles cases where the initial service-endpoints event is missed
  }

  private static getWwwEnv = () => {
    const { host } = document.location;
    const found = ServiceEndpointsUtil.wwwDomainToEnvMap.find((val) => val.from === host);
    return found && found.to ? found.to : 'live';
  };

  private static getCadEnv = () => {
    const wwwEnv = ServiceEndpointsUtil.getWwwEnv();
    const found = ServiceEndpointsUtil.wwwEnvToCadEnvMap.find((val) => val.from === wwwEnv);
    return found && found.to ? found.to : 'live';
  };

  private static getProductDomain = (): string => {
    const cadEnv = ServiceEndpointsUtil.getCadEnv();
    const found = ServiceEndpointsUtil.cadEnvToCadDomainMap.find((val) => val.from === cadEnv);
    return found && found.to ? found.to : 'cad.onshape.com';
  };

  private static getSsoCadDomain = (): string => {
    const cadEnv = ServiceEndpointsUtil.getCadEnv();
    const cadSsoDomainMap = ServiceEndpointsUtil.cadEnvToCadSsoDomainMap;
    const found = cadSsoDomainMap?.find((val) => val.from === cadEnv);
    return found && found.to ? found.to : 'sso.onshape.com';
  };

  public static mapDomain = (str: string) => {
    if (ExternalUtil.isProduction()) return str;

    const isIncludedInDomainMapping = ServiceEndpointsUtil.cadEnvToCadDomainMap.some((mapping) =>
      str.includes(`${mapping.to}`)
    );
    const isIncludedInSsoDomainMapping = ServiceEndpointsUtil.cadEnvToCadSsoDomainMap.some(
      (mapping) => str.includes(`${mapping.to}`)
    );
    if (!isIncludedInDomainMapping && !isIncludedInSsoDomainMapping) return str;

    const from = new URL(str);

    let to;
    if (from.host.includes('sso')) {
      to = new URL(`https://${ServiceEndpointsUtil.getSsoCadDomain()}`);
    } else {
      to = new URL(`https://${ServiceEndpointsUtil.getProductDomain()}`);
    }

    from.host = to.host;
    return from.toString().replace(/\/$/, ''); // Remove trailing slash if exists
  };
}
